import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid"; // veya outline

const HeatmapComponent = React.memo(({ totalData, themeMode }) => {
  const topMenuTabs = useSelector((state) => state.topMenuTabs);
  const currentSurvey = topMenuTabs.tabs.find(
    (tab) => tab.id === topMenuTabs.activeTab
  ).states.currentSurvey;

  const [heatMapData, setHeatMapData] = useState();
  const [series, setSeries] = useState([]);
  const [heatMapDataIndex, setHeatMapDataIndex] = useState({
    index: 0,
    text: "",
  });
  const [prevPageState, setPrevPageState] = useState(false);
  const chartRef = useRef(null);
  const [chartReady, setChartReady] = useState(false);
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true);
  const [title, setTitle] = useState({ title: "", childTitle: "" });
  const [questionSearchTerm, setQuestionSearchTerm] = useState("");

  const [selectedQuestion, setSelectedQuestion] = useState("Select a question");
  const [allQuestions, setAllQuestions] = useState(totalData);
  const [mounted, setMounted] = useState(false);

  // İlk render sonrası setMounted'i true yapıyoruz
  useEffect(() => {
    setMounted(true);
  }, []);
  const handlePrevClick = () => {
    if (selectedParam.param === "section") {
      setSelectedParam({ param: "", value: selectedParam.value });
      setPrevButtonDisabled(true);
      setAllQuestions(totalData);
      setSelectedQuestion(totalData[0].question);
      setChartOptions((prevOptions) => ({
        ...prevOptions,
        title: {
          ...prevOptions.title,
          text: "",
        },
      }));
    } else if (selectedParam.param === "page") {
      const index = totalData.findIndex(
        (item) => item.page === selectedParam.value
      );
      const sectionForSelectedValue = totalData.find(
        (item) => item.page === selectedParam.value
      )?.section;
      setChartOptions((prevOptions) => ({
        ...prevOptions,
        title: {
          ...prevOptions.title,
          text: title.title,
        },
      }));
      setSelectedQuestion(totalData[index].question);
      setSelectedParam({ param: "section", value: sectionForSelectedValue });
      setPrevPageState(true);
    }
  };
  const formatXaxisLabels = (labels) => {
    return labels.map((label) => {
      const words = label.split(" ");
      const formattedLabel = [];
      for (let i = 0; i < words.length; i += 2) {
        formattedLabel.push(words.slice(i, i + 2).join(" "));
      }
      return formattedLabel;
    });
  };

  const uniqueSections = useMemo(() => {
    if (!totalData || totalData.length === 0) {
      return []; // Boş bir array dönebiliriz
    }

    return formatXaxisLabels([
      ...new Set(
        totalData
          .filter(
            (x) =>
              x.section.toLowerCase() !==
              currentSurvey?.content?.pages
                .find((y) => y.type === "demographics")
                ?.name?.toLowerCase()
          )
          .map((item) => item.section)
      ),
    ]);
  }, [totalData, currentSurvey]);

  const [xAxisCategories, setXAxisCategories] = useState([]);
  const [selectedParam, setSelectedParam] = useState({ param: "", value: "" });
  const selectedParamRef = useRef(selectedParam);
  const transformToHeatmapData = (data) => {
    const currentSelectedParam = selectedParamRef.current;
    const yCategories = data.options
      .filter((opt) => {
        let scores = null;
        if (selectedParam.param === "") {
          scores = opt.scores["section"];
        } else if (selectedParam.param === "section") {
          scores = opt.scores["page"];
        } else if (selectedParam.param === "page") {
          scores = opt.scores["question"];
        }
        return scores && Object.keys(scores).length !== 0; // scores boş olmasın
      })
      .map((opt) => opt.opt);

    let optionsLen = data.options.lenght;
    const seriesData = [];
    for (let i = 0; i < data.options.length; i++) {
      const opt = data.options[i];
      let scores = null;

      if (selectedParam.param === "") {
        scores = opt.scores["section"];
      } else if (selectedParam.param === "section") {
        scores = opt.scores["page"];
      } else if (selectedParam.param === "page") {
        scores = opt.scores["question"];
      }

      let dataPoints = [];

      if (!scores || Object.keys(scores).length === 0) {
        // Eğer scores boşsa, bu öğeyi atla
        continue;
      } else if (
        selectedParam.param === "page" ||
        selectedParam.param === "section"
      ) {
        dataPoints = Object.entries(scores)
          .map(([category, subScores]) => {
            if (!prevPageState) {
              if (category === heatMapDataIndex.text) {
                return Object.entries(subScores).map(([question, val]) => ({
                  x: question,
                  y: val,
                }));
              }
              return [];
            } else {
              return heatMapDataIndex.text in subScores
                ? Object.entries(subScores).map(([key, val]) => ({
                    x: key,
                    y: val,
                  }))
                : [];
            }
          })
          .flat(); // Array'i düzleştiriyoruz

      } else {
        dataPoints = Object.entries(scores).map(([key, val]) => ({
          x: key, // Bölüm adı
          y: val, // Puan
        }));
      }


      // Sonuçları seriesData'ya ekle
      if (dataPoints.length > 0) {
        seriesData.push({
          name: opt.opt,
          data: dataPoints,
        });
      }
    }

    return {
      yAxis: { categories: yCategories },
      series: seriesData,
    };
  };
  useEffect(() => {
    if (totalData.length > 0) {
      const demographicsSection = currentSurvey?.content?.pages.find(
        (page) => page.type === "demographics"
      );

      // `filteredData` içerisine "demographics" ile eşleşen ilk öğeyi ekliyoruz
      const firstDemographicItem = totalData.find(
        (item) =>
          item.section.toLowerCase() ===
          demographicsSection?.name?.toLowerCase()
      );

      if (firstDemographicItem) {
        setHeatMapData(transformToHeatmapData(firstDemographicItem));
        setSelectedQuestion(firstDemographicItem.question);
      }
    }
  }, []);
  const darkenColor = (hex, percent) => {
    let r = parseInt(hex.substring(1, 3), 16);
    let g = parseInt(hex.substring(3, 5), 16);
    let b = parseInt(hex.substring(5, 7), 16);

    r = Math.max(0, Math.floor(r * (1 - percent)));
    g = Math.max(0, Math.floor(g * (1 - percent)));
    b = Math.max(0, Math.floor(b * (1 - percent)));

    return `#${r.toString(16).padStart(2, "0")}${g
      .toString(16)
      .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };
  const interpolateColor = (color1, color2, factor) => {
    let result = color1
      .slice(1)
      .match(/.{2}/g)
      .map((hex) => parseInt(hex, 16));
    let target = color2
      .slice(1)
      .match(/.{2}/g)
      .map((hex) => parseInt(hex, 16));
  
    let interpolated = result.map((comp, i) =>
      Math.round(comp + (target[i] - comp) * factor)
    );
  
    return `#${interpolated
      .map((comp) => comp.toString(16).padStart(2, "0"))
      .join("")}`;
  };
  
  const generateColorRange = (startColor, midColor, endColor, minValue, maxValue, steps) => {
    const preciseSteps = steps * 10;
    const colorRanges = [];
    const stepSize = (maxValue - minValue) / preciseSteps;
  
    for (let i = 0; i < preciseSteps; i++) {
      let from = minValue + i * stepSize;
      let to = from + stepSize;
      let color;
  
      if (from <= 2) {
        color = startColor; // 1.5 ve altı için kırmızı
      } else if (from >= 4.5) {
        color = endColor; // 4.5 ve üstü için yeşil
      } else if (from > 2 && from < 3.25) {
        // Kırmızıdan sarıya geçiş (1.5 - 3 arası)
        let factor = (from - 2) / (3.25 - 2);
        color = interpolateColor(startColor, midColor, factor);
      } else {
        // Sarıdan yeşile geçiş (3 - 4.5 arası)
        let factor = (from - 3.25) / (4.5 - 3.25);
        color = interpolateColor(midColor, endColor, factor);
      }
  
      colorRanges.push({
        from: parseFloat(from.toFixed(4)),
        to: parseFloat(to.toFixed(4)),
        color: color,
      });
    }
  
    return colorRanges;
  };
  
  const startColor = "#cc0000"; // Kırmızı
  const midColor = "#d5d500"; // Sarı
  const endColor = "#005c08"; // Yeşil
  const minValue = 0;
  const maxValue = 5;
  const steps = 1999;
  
  const colorRanges = generateColorRange(startColor, midColor, endColor, minValue, maxValue, steps);

  const options = {
    chart: {
      type: "heatmap",
      zoom: {
        enabled: true, // Yatayda zoom yapılabilir
        type: "x", // Sadece x ekseninde zoom
        autoScaleYaxis: true, // Y ekseni otomatik ölçeklenir
      },
      events: {
        mounted: function () {
          setChartReady(true);
          const rectElements = document.querySelectorAll(
            ".apexcharts-heatmap-rect"
          );
          rectElements.forEach((rect) => {
            const rectColor = rect.getAttribute("fill"); // Hücrenin arka plan rengi (fill)

            // Hücrenin metnini alıyoruz (text)
            const label = rect.querySelector(".apexcharts-datalabel");

            if (label) {
              // Koyu renk hesaplama
              const darkenedColor = darkenColor(rectColor, 20); // %20 koyulaştırma
              label.setAttribute("fill", darkenedColor); // Metnin rengini koyulaştırılmış renge ayarla
            }
          });
        },
        click: function (event, chartContext, config) {
          setPrevButtonDisabled(false);
          const clickedElement = event.target;
          const text = clickedElement.textContent || clickedElement.innerText;
          const parentElement = clickedElement.parentElement;
          if (
            parentElement &&
            parentElement.classList.contains("apexcharts-text") &&
            parentElement.classList.contains("apexcharts-xaxis-label")
          ) {
            const currentSelectedParam = selectedParamRef.current;
            setPrevPageState(false);
            setSelectedQuestion("Select a question");
            if (currentSelectedParam.param === "") {
              // if (!text) {
              //   console.error("Hata: text değeri tanımlı değil veya boş!");
              // }
              const cleanedText = text.replace(/\.+$/, ""); // Sonundaki noktaları kaldır
              const index = totalData.findIndex((item) =>
                item.section?.startsWith(cleanedText)
              );
              const matchingData = totalData.filter(
                (item) => item.section === text
              );

              setAllQuestions(matchingData);
              if (index !== -1) {
                setSelectedQuestion(totalData[index].question);
                setHeatMapDataIndex({
                  index: index,
                  text: totalData[index].section,
                });
                setTitle({ title: totalData[index].section, childTitle: "" });
                setSelectedParam({
                  param: "section",
                  value: totalData[index].section,
                });
              } else {
                console.warn("Eşleşen veri bulunamadı:");
              }
            } else if (currentSelectedParam.param === "section") {
              const cleanedText = text.replace(/\.+$/, "");
              const index = totalData.findIndex((item) =>
                item.page?.startsWith(cleanedText)
              );

              const matchingData = totalData.filter((item) =>
                item.page.startsWith(text)
              );

              setAllQuestions(matchingData);
              if (index !== -1) {
                setSelectedQuestion(totalData[index].question);
                setHeatMapDataIndex({
                  index: index,
                  text: totalData[index].page,
                });
                setTitle((prevState) => ({
                  ...prevState, // Mevcut objeyi koru
                  childTitle: totalData[index].page, // Sadece childTitle'ı güncelle
                }));
                setSelectedParam({
                  param: "page",
                  value: totalData[index].page,
                });
              } else {
                console.warn("Eşleşen veri bulunamadı:");
              }
            }
          } else {
          }
        },
      },
    },
    title: {
      text: "",
      align: "center  ",
      offsetX: 50,
      style: {
        color: themeMode === "dark" ? "#fff" : "#000", // Koyu temada beyaz, açık temada siyah
        fontSize: "16px", // Yazı Boyutu
        fontFamily: "Arial",
        fontWeight: "bold",
      },
    },
    legend: {
      show: false, // Legend'i gizler
    },
    toolbar: {
      show: true,
      tools: {
        download: false,
        zoom: false,
        pan: false,
        reset: false,
      },
    },
    tooltip: {
      enabled: true,
      style: {
        fontSize: "14px",
        fontFamily: "Arial, sans-serif",
        color: themeMode === "dark" ? "#fff" : "#000",
        whiteSpace: "normal",
        wordWrap: "break-word",
      },
      x: {
        formatter: (value) => {
          if (selectedParam.param === "") {
            return `Section: ${value}`;
          } else if (selectedParam.param === "section") {
            return `Page: ${value}`;
          } else if (selectedParam.param === "page") {
            return `Question: ${value}`;
          }
          return value;
        },
      },
      y: {
        formatter: (value) => `Value: ${value.toFixed(2)}`,
      },
      theme: themeMode === "dark" ? "dark" : "light",
    },
    plotOptions: {
      heatmap: {
        colorScale: {
          ranges: colorRanges,
        },
      },
    },
    xaxis: {
      type: "category",
      position: "top",
      categories: xAxisCategories,
      labels: {
        style: {
          colors: themeMode === "dark" ? "#fff" : "#000",
          fontSize: "12px",
          fontFamily: "Arial",
          cursor: "pointer",
          pointerEvents: "auto!important",
          // textOverflow: 'ellipsis', // Text overflow engelleyebiliriz
        },
        formatter: (val) => {
          // Etiketi 10 karakterden fazla ise kısalt
          return val.length > 10 ? val.substring(0, 10) + "..." : val;
        },
        rotate: selectedParam.param === "question" ? -90 : 0,
        padding: 15,
      },
      grid: {
        show: true,
        borderColor: themeMode === "dark" ? "#555" : "#ccc", // Grid için sınır rengi
        strokeDashArray: 5,
        position: "back",
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: themeMode === "dark" ? "#fff" : "#000",
          fontSize: "12px",
          fontFamily: "Arial",
          backgroundColor: themeMode === "dark" ? "#333" : "#f4f4f4",
        },
        textOverflow: "clip",
      },
      formatter: (value) => {
        return value.toString().split(" ").join("\n");
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "18px",
        fontFamily: "Arial, sans-serif",
        fontWeight: 600,
      },
      formatter: function (val) {
        return val.toFixed(2);
      },
    },
  };
  const [chartOptions, setChartOptions] = useState(options);
  useEffect(() => {
    if (!mounted) return;
    let newXAxisCategories = uniqueSections;

    if (selectedParam.param === "") {
      newXAxisCategories = uniqueSections;
    } else if (selectedParam.param === "section") {
      let pagesForSelectedSection;

      if (prevPageState === false) {
        pagesForSelectedSection = formatXaxisLabels([
          ...new Set(
            totalData
              .filter((item) => item.section === selectedParam.value)
              .map((item) => item.page)
          ),
        ]);
      } else if (prevPageState === true) {
        // const sectionForSelectedValue = totalData.find(
        //   (item) => item.page === selectedParam.value
        // )?.section;
        const matchingData = totalData.filter(
          (item) => item.section === selectedParam.value
        ); // o section'a sahip sayfaları al

        setAllQuestions(matchingData);
        if (selectedParam.value) {
          pagesForSelectedSection = formatXaxisLabels([
            ...new Set(
              totalData
                .filter((item) => item.section === selectedParam.value) // o section'a sahip sayfaları al
                .map((item) => item.page) // sayfa numaralarını al
            ),
          ]);
        }
      }
      newXAxisCategories = pagesForSelectedSection;
    } else if (selectedParam.param === "page") {
      const pagesForSelectedSection = [
        ...new Set(
          totalData
            .filter((item) => item.page === selectedParam.value)
            .map((item) => item.question)
        ),
      ];
      const matchingData = totalData.filter(
        (item) => item.page === selectedParam.value
      );

      setAllQuestions(matchingData);
      newXAxisCategories = pagesForSelectedSection;
    }
    setXAxisCategories(newXAxisCategories);
    setHeatMapData(transformToHeatmapData(totalData[heatMapDataIndex.index]));
    setChartOptions((prevOptions) => ({
      ...prevOptions,
      xaxis: {
        ...prevOptions.xaxis,
        categories: newXAxisCategories,
      },
    }));
    selectedParamRef.current = selectedParam;
  }, [
  selectedParam,
  heatMapDataIndex,
  totalData,
  uniqueSections,
  prevPageState,
  mounted, // Eğer ref değilse
]);
  useEffect(() => {
    setChartOptions((prevOptions) => ({
      ...prevOptions,
      xaxis: {
        ...prevOptions.xaxis, // xaxis'in diğer özelliklerini koru
        labels: {
          ...prevOptions.xaxis.labels, // labels'ın diğer özelliklerini koru
          style: {
            ...prevOptions.xaxis.labels.style, // style içindeki diğer özellikleri koru
            colors: themeMode === "dark" ? "#fff" : "#000", // Burada newColor, state'den gelen veya güncellemek istediğiniz yeni renk değeri
          },
        },
      },
      yaxis: {
        ...prevOptions.yaxis,
        labels: {
          ...prevOptions.yaxis.labels,
          style: {
            ...prevOptions.yaxis.labels.style,
            colors: themeMode === "dark" ? "#fff" : "#000",
          },
        },
      },
      tooltip: {
        ...prevOptions.tooltip,
        style: {
          ...prevOptions.tooltip.style,
          color: themeMode === "dark" ? "#fff" : "#000",
        },
        theme: themeMode === "dark" ? "dark" : "light",
      },
      title: {
        ...prevOptions.title,
        style: {
          ...prevOptions.title.style,
          color: themeMode === "dark" ? "#fff" : "#000",
        },
      },
    }));
  }, [themeMode]);
  const [chartWidth, setChartWidth] = useState(800); // Varsayılan genişlik
  useEffect(() => {
    if (heatMapData) {
      const variable =
        selectedParam.param === ""
          ? "section"
          : selectedParam.param === "section"
          ? "page"
          : "question";
      // console.log
      const updatedSeries = heatMapData.series.map((item) => ({
        ...item,
        data: item.data.filter((data) => {
          const isDemographic =
            currentSurvey?.content?.pages
              .find((y) => y.type === "demographics")
              ?.name.toLowerCase() === data.x.toLowerCase();

          let isInTotalData = false;
          totalData.forEach((td, tdIndex) => {
            if (td[variable] === data.x) {
              isInTotalData = true;
            }
          });

          const shouldKeep = !isDemographic && isInTotalData;

          return !isDemographic && isInTotalData;
        }),
      }));

      setSeries(updatedSeries);
    }
  }, [heatMapData]);
  useEffect(() => {
    if (title.title !== "") {
      setChartOptions((prevOptions) => ({
        ...prevOptions,
        title: {
          ...prevOptions.title,
          text:
            title.childTitle !== ""
              ? title.title + ">" + title.childTitle
              : title.title,
        },
      }));
    }
  }, [title]);
  useEffect(() => {
    const updateWidth = () => {
      if (window.innerWidth >= 1736) {
        setChartWidth(1300); // 2xl (1536px ve üstü) için 1000px genişlik
      } else if (window.innerWidth >= 1638) {
        setChartWidth(1200);
      } else if (window.innerWidth >= 1538) {
        setChartWidth(1100);
      } else if (window.innerWidth >= 1438) {
        setChartWidth(950); // xl (1280px - 1536px) için 900px genişlik
      } else if (window.innerWidth >= 1338) {
        setChartWidth(900); // xl (1280px - 1536px) için 900px genişlik
      } else if (window.innerWidth >= 1280) {
        setChartWidth(850); // xl (1280px - 1536px) için 900px genişlik
      } else if (window.innerWidth >= 1194) {
        setChartWidth(770); // lg (1024px - 1280px) için 800px genişlik
      } else if (window.innerWidth >= 1124) {
        setChartWidth(650); // lg (1024px - 1280px) için 800px genişlik
      } else if (window.innerWidth >= 1024) {
        setChartWidth(600); // lg (1024px - 1280px) için 800px genişlik
      } else if (window.innerWidth >= 768) {
        setChartWidth(550); // md (768px - 1024px) için 700px genişlik
      } else if (window.innerWidth >= 600) {
        setChartWidth(570); // Küçük ekranlar için 600px genişlik
      } else if (window.innerWidth >= 550) {
        setChartWidth(450); // Küçük ekranlar için 600px genişlik
      }
    };
    updateWidth(); // Sayfa açıldığında çalıştır
    window.addEventListener("resize", updateWidth); // Resize event'i dinle

    return () => window.removeEventListener("resize", updateWidth); // Cleanup
  }, []);

  return (
    <div
      className={`shadow-md rounded-lg pt-2 px-0 pb-0 mt-4 flex flex-col w-full mr-4
                        ${
                          themeMode === "dark"
                            ? "text-white cursor-pointer "
                            : "text-gray-800 cursor-pointer"
                        }`}
    >
      <div className="flex  space-x-4  ">
        <button
          disabled={prevButtonDisabled}
          className={`w-16 h-6  ml-4 flex items-center pointer-events-auto text-center font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 text-white ${
            prevButtonDisabled
              ? "cursor-not-allowed bg-gray-400 cursor-not-allowed "
              : "cursor-pointer bg-blue-700 hover:bg-blue-800 text-white"
          } `}
          onClick={handlePrevClick}
        >
          Back
        </button>
        <Menu as="div" className="relative inline-block text-left ml-0">
          <div>
            <MenuButton
              className={`flex items-center self-stretch p-0 pr-1 h-[1.5rem] lg:h-[2rem] min-w-8  rounded-md shadow-inner cursor-text  ${
                themeMode === "dark"
                  ? "bg-white-02"
                  : "bg-black-02 shadow-[0_0_0_1px_rgba(0,0,0,0.09)]"
              } `}
            >
              <div
                className={`hulyButton font-medium-14 h-[1.5rem] lg:h-[2rem] w-auto px-2 border rounded-[0.375rem] cursor-pointer flex justify-center  items-center gap-2 ${
                  themeMode === "dark"
                    ? "border-[#d1d5de0a] bg-[#d1d5de0d] hover:bg-[#d1d5de0a] hover:text-[#fff] text-white-8"
                    : "border-[#1725471a] bg-[#1725470d] hover:bg-[#1725471a] hover:text-[#000] text-black-8"
                }`}
                data-id="btn-viewSetting"
              >
                <span
                  className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0 text-xs lg:text-sm"
                  style={{ maxWidth: "200px" }}
                >
                  {selectedQuestion}
                </span>
                <div
                  className={`icon no-gap  inline-flex justify-center  items-center  ${
                    themeMode === "dark" ? "text-[#ffffff]" : "text-[#000]"
                  }`}
                >
                  <ChevronDownIcon className="size-4 lg:size-5" />
                </div>
              </div>
            </MenuButton>
          </div>

          <MenuItems
            transition
            className={`absolute left-1 z-[99999999]  w-64 h-[250px]  origin-top-right rounded-md shadow-2xl ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in  border-solid border-[1px] ${
              themeMode === "dark"
                ? " bg-[#2a2939] border-white-09 shadow-theme-popup-shadow "
                : "bg-[#FFFFFF] border-black-09 shadow-black-2-shadow"
            }`}
          >
            <div className="px-1 py-2">
              <div className="px-4">
                <input
                  type="text"
                  placeholder="Search..."
                  value={questionSearchTerm}
                  onChange={(e) => setQuestionSearchTerm(e.target.value)}
                  className={`w-full px-2 py-1 mb-2 rounded-md ${
                    themeMode === "dark"
                      ? " bg-[#2a2939]  shadow-theme-popup-shadow  placeholder:text-white text-white"
                      : "bg-[#FFFFFF] shadow-black-2-shadow placeholder:text-black text-black"
                  }`}
                />
              </div>
              <div className="flex-grow min-h-0 h-max overflow-x-hidden">
                <div className="flex flex-col  overflow-y-auto h-[200px]">
                  {allQuestions
                    .filter(
                      (option) =>
                        !questionSearchTerm.trim() || // Eğer arama terimi boşsa tüm öğeleri döndür
                        option.question
                          .toLowerCase()
                          .includes(questionSearchTerm.toLowerCase())
                    )
                    .map((option, index) => (
                      <div
                        key={option.question_id}
                        className="cursor-pointer"
                        onClick={() => {
                          const index = totalData.findIndex(
                            (item) => item.question_id === option.question_id
                          );
                          setHeatMapData(
                            transformToHeatmapData(totalData[index])
                          );
                          setSelectedQuestion(option.question);
                        }}
                      >
                        <div
                          className={`flex-shrink-0 flex justify-between items-center px-2 py-1 min-w-0 min-h-[2.25rem] text-left rounded-md cursor-pointer ${
                            selectedQuestion === option.question
                              ? themeMode === "dark"
                                ? "bg-[#333240] text-white"
                                : "bg-[#EBEBEB] text-black"
                              : themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          }`}
                        >
                          <label
                            className={`ms-3 text-sm font-medium cursor-pointer ${
                              themeMode === "dark"
                                ? "text-[#f7f8f8]"
                                : "text-[#131416]"
                            }`}
                          >
                            {option.question}
                          </label>
                          {selectedQuestion !== "Select a question" &&
                            option.question === selectedQuestion && (
                              <div className="pointer-events-none">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="3"
                                  stroke="currentColor"
                                  className="w-4 h-4"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m4.5 12.75 6 6 9-13.5"
                                  />
                                </svg>
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </MenuItems>
        </Menu>
      </div>
      <div ref={chartRef}>
        <ReactApexChart
          className="flex flex-col w-full"
          options={chartOptions}
          series={series}
          type="heatmap"
          height={350}
          // width={chartWidth}
        />
      </div>
    </div>
  );
});

export default HeatmapComponent;
